<template>
  <div class="bert-input-wrapper">
    <label v-if="label" :for="inputId" class="bert-input-label" :class="{ 'bert-input-label--required': required }">
      {{ label }}
    </label>
    
    <div class="bert-input-container" :class="containerClasses">
      <span v-if="iconLeft" class="bert-input-icon bert-input-icon--left">
        <slot name="icon-left"></slot>
      </span>
      
      <input
        :id="inputId"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :class="inputClasses"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
        v-bind="$attrs"
      />
      
      <span v-if="iconRight" class="bert-input-icon bert-input-icon--right">
        <slot name="icon-right"></slot>
      </span>
    </div>
    
    <div v-if="error || hint" class="bert-input-message">
      <span v-if="error" class="bert-input-error">{{ error }}</span>
      <span v-else-if="hint" class="bert-input-hint">{{ hint }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BertInput',
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    iconLeft: {
      type: Boolean,
      default: false
    },
    iconRight: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      focused: false,
      inputId: `bert-input-${Math.random().toString(36).substr(2, 9)}`
    }
  },
  computed: {
    containerClasses() {
      return [
        'bert-input-container',
        `bert-input-container--${this.size}`,
        {
          'bert-input-container--focused': this.focused,
          'bert-input-container--error': this.error,
          'bert-input-container--disabled': this.disabled,
          'bert-input-container--with-icon-left': this.iconLeft,
          'bert-input-container--with-icon-right': this.iconRight
        }
      ]
    },
    inputClasses() {
      return [
        'bert-input',
        `bert-input--${this.size}`
      ]
    }
  },
  methods: {
    handleInput(event) {
      this.$emit('input', event.target.value)
    },
    handleFocus(event) {
      this.focused = true
      this.$emit('focus', event)
    },
    handleBlur(event) {
      this.focused = false
      this.$emit('blur', event)
    },
    handleKeydown(event) {
      this.$emit('keydown', event)
    }
  }
}
</script>

<style scoped>
.bert-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.bert-input-label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--neutral-700);
}

.bert-input-label--required::after {
  content: ' *';
  color: var(--error-600);
}

.bert-input-container {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid var(--neutral-300);
  border-radius: var(--radius-md);
  background-color: white;
  transition: all var(--transition-base);
}

.bert-input-container:hover:not(.bert-input-container--disabled) {
  border-color: var(--neutral-400);
}

.bert-input-container--focused {
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

.bert-input-container--error {
  border-color: var(--error-500);
}

.bert-input-container--error.bert-input-container--focused {
  box-shadow: 0 0 0 3px var(--error-100);
}

.bert-input-container--disabled {
  background-color: var(--neutral-100);
  border-color: var(--neutral-200);
  cursor: not-allowed;
}

/* Sizes */
.bert-input-container--sm {
  min-height: 32px;
}

.bert-input-container--md {
  min-height: 40px;
}

.bert-input-container--lg {
  min-height: 48px;
}

.bert-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-family-sans);
  color: var(--neutral-800);
}

.bert-input--sm {
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
}

.bert-input--md {
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-base);
}

.bert-input--lg {
  padding: var(--space-4) var(--space-5);
  font-size: var(--text-lg);
}

.bert-input::placeholder {
  color: var(--neutral-400);
}

.bert-input:disabled {
  cursor: not-allowed;
  color: var(--neutral-500);
}

.bert-input-container--with-icon-left .bert-input {
  padding-left: var(--space-2);
}

.bert-input-container--with-icon-right .bert-input {
  padding-right: var(--space-2);
}

.bert-input-icon {
  display: flex;
  align-items: center;
  color: var(--neutral-500);
}

.bert-input-icon--left {
  padding-left: var(--space-3);
}

.bert-input-icon--right {
  padding-right: var(--space-3);
}

.bert-input-message {
  font-size: var(--text-sm);
}

.bert-input-error {
  color: var(--error-600);
}

.bert-input-hint {
  color: var(--neutral-500);
}
</style>